import React, { useEffect, useMemo, useState } from "react"
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Select,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react"
import axios from "axios"
import { formatAUDate, formatAUTime, getBusinessTimeZone, setBusinessTimeZone } from "../../global-client"

const fallbackTimeZones = [
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Adelaide",
  "Australia/Darwin",
  "Australia/Perth",
  "Australia/Hobart",
  "Pacific/Auckland",
  "Asia/Singapore",
  "Europe/London",
  "America/New_York",
  "UTC",
]

const getTimeZoneOptions = () => {
  if (typeof Intl?.supportedValuesOf === "function") {
    try {
      const zones = Intl.supportedValuesOf("timeZone")
      if (Array.isArray(zones) && zones.length > 0) {
        return zones
      }
    } catch (error) {
      // Fallback list is used when the runtime cannot enumerate all zones.
    }
  }

  return fallbackTimeZones
}

const SetupGeneral = () => {
  const toast = useToast()
  const [businessTimeZone, setBusinessTimeZoneState] = useState(getBusinessTimeZone())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const timeZoneOptions = useMemo(() => getTimeZoneOptions(), [])

  useEffect(() => {
    const fetchSetup = async () => {
      try {
        setLoading(true)
        const { data } = await axios.get("/api/setup")

        if (data?.businessTimeZone) {
          setBusinessTimeZoneState(data.businessTimeZone)
          setBusinessTimeZone(data.businessTimeZone)
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load general setup",
          status: "error",
          duration: 3000,
          isClosable: true,
        })
      } finally {
        setLoading(false)
      }
    }

    fetchSetup()
  }, [toast])

  const handleSave = async () => {
    try {
      setSaving(true)
      const { data } = await axios.put("/api/setup", {
        businessTimeZone,
      })

      if (data?.businessTimeZone) {
        setBusinessTimeZone(data.businessTimeZone)
        setBusinessTimeZoneState(data.businessTimeZone)
      }

      toast({
        title: "Saved",
        description: "General setup updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to save general setup",
        status: "error",
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  const previewDate = useMemo(() => new Date(), [])

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack spacing={6} align='stretch'>
        <Heading size='lg'>General Setup</Heading>

        <Alert status='info'>
          <AlertIcon />
          All app dates and times are displayed using the selected business timezone.
        </Alert>

        <FormControl isDisabled={loading || saving}>
          <FormLabel fontSize='md' fontWeight='semibold'>
            Business Timezone
          </FormLabel>
          <Select value={businessTimeZone} onChange={(e) => setBusinessTimeZoneState(e.target.value)}>
            {timeZoneOptions.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </Select>
          <FormHelperText>
            Choose the timezone where your business operates. Job and booking times will always use this timezone.
          </FormHelperText>
        </FormControl>

        <Box borderWidth='1px' borderRadius='md' p={4}>
          <Text fontWeight='semibold' mb={2}>
            Preview
          </Text>
          <Text>
            Current display: {formatAUDate(previewDate)} at {formatAUTime(previewDate)}
          </Text>
          <Text color='gray.500' fontSize='sm' mt={1}>
            Timezone: {businessTimeZone}
          </Text>
        </Box>

        <HStack>
          <Button colorScheme='blue' onClick={handleSave} isLoading={saving} isDisabled={loading}>
            Save Changes
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

export default SetupGeneral
